<template>
  <slot
    :answer="answer"
    :loading="loading"
  />
</template>

<script setup lang="ts">
import { useRenderNotifier } from '@wui/common-library'
import { computed, ref, watch } from 'vue'

import { grantMicrophonePermission } from '@/features/mic-permission/model'

import { useCallSessionRoomStore } from '@/entities/call-session'
import { useCallSessionMediaStore } from '@/entities/call-session-media'

import { useLocalization } from '@/shared/composables/localization'
import { useServiceWorker } from '@/shared/composables/service-worker'
import { RTCSessionFacade, STATE, useWebRTC } from '@/shared/services/jssip'
import { getAnswerOptions } from '@/shared/utils/jssip'

const props = defineProps<{
  sessionId: string
}>()

const { getRTCSessionFacadeById, sessions } = useWebRTC()
const { notifyError } = useRenderNotifier()
const { t } = useLocalization()
const { closeServiceWorkerNotification } = useServiceWorker()
const { findBySessionIdAndParticipant } = useCallSessionMediaStore()

const facade = computed<RTCSessionFacade | undefined>(() => getRTCSessionFacadeById(props.sessionId))
const otherCallSession = computed(() => Array.from(sessions.value.values()).filter((item) => Number(item.session.status) === STATE.ERROR))
const state = computed(() => facade.value?.sessionState.value)

const loading = ref(false)
const isVideoAvailable = computed(() => {
  if (!facade.value) {
    return false
  }
  const room = useCallSessionRoomStore().getRoom(facade.value.sessionId)
  return !!room?.isGroup || findBySessionIdAndParticipant(facade.value.sessionId, facade.value.number)?.video
})

const answer = async (audio: boolean, video: boolean) => {
  if (loading.value || !facade.value) {
    return
  }

  try {
    if (!await grantMicrophonePermission()) {
      return
    }

    loading.value = true
    const options = await getAnswerOptions(audio, video, isVideoAvailable.value)
    facade.value.isVideoMuted.value = !video
    facade.value.answer(options)

    closeServiceWorkerNotification(props.sessionId)
    otherCallSession.value.forEach((sessionFacade) => {
      if (sessionFacade.sessionState.value === STATE.RINGING) {
        if (sessionFacade.dialog.closeDialog.value) {
          sessionFacade.dialog.closeDialog.value(undefined)
        }
      }
    })
  } catch (e: unknown) {
    notifyError(e instanceof Error ? e.message : t('common.unknownError'))
    console.error(e)
    loading.value = false
  }
}

watch(() => loading.value, (value) => {
  if (value && state.value === STATE.CONNECTED) {
    loading.value = false
  }
})
</script>
